import { Injectable } from '@angular/core';
import { Store, Action, select } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, forkJoin, of, throwError, Subscription } from 'rxjs';
import { catchError, map, concatMap, mergeMap, switchMap, withLatestFrom, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

import * as Objects from '@core/objects';
import { VersionShareItem } from '@core/models/version-share.model';

import * as Access from '@app/models/access';

import { ProjectsRequests } from './projects.requests';

import * as fromStore from './projects.state';
import * as fromActions from './projects.actions';

import * as  fromAppStore from '../app/app.state';
import * as fromDashboardActions from '../dashboard/dashboard.actions';

@Injectable()
export class ProjectsEffects {

    protected readonly PLUGIN_REQUEST_FEEDBACK = 'share_feedback';

    constructor(private actions$: Actions, private store$: Store<fromStore.Projects>,
        private appStore$: Store<fromAppStore.App>,
        private requests: ProjectsRequests) { }


    public loadProject$: Observable<fromActions.ProjectsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadProject>(fromActions.ProjectsActionType.LoadProject),
        switchMap((action) => {
            return this.requests.load$(action.projectId).pipe(
                map((response: HttpResponse<Objects.Request>) => {
                    if (response.status === 200) {
                        // Full success
                        return new fromActions.LoadProjectSuccess(response.body);
                    } else if (response.status === 206) {
                        // Partial content: User has limited access, set project header instead
                        return new fromActions.LoadPartialProjectSuccess(response.body);
                    }
                    // Fallback for unexpected status codes (shouldn't happen with catchError)
                    throw new Error(`Unexpected status: ${response.status}`);
                }),
                catchError((err: HttpErrorResponse) => {
                    console.log('Error loading project:', err);
                    return of(new fromActions.LoadProjectError(err, action.projectId));
                })
            );
        })
    ));

    public loadProjectWithUploaded$: Observable<fromActions.ProjectsActions> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadWithUploadedVersions>(fromActions.ProjectsActionType.LoadWithUploadedVersions),
        switchMap((action) =>
            this.requests.load$(action.projectId).pipe(
                map((response: HttpResponse<Objects.Request>) => {
                    if (response.status === 200) {
                        // Only dispatch success if we have full access (200 OK)
                        return new fromActions.LoadWithUploadedVersionsSuccess(response.body);
                    }
                    // If not 200 (e.g., 206 or other success codes), treat as error
                    throw new HttpErrorResponse({
                        error: `Expected 200 OK, got ${response.status}`,
                        status: response.status,
                        statusText: response.statusText
                    });
                }),
                catchError((err: HttpErrorResponse) => {
                    console.log('Error loading project with uploaded versions:', err);
                    return of(new fromActions.LoadWithUploadedVersionsError(err, action.projectId));
                })
            )
        )
    ));

    public loadProjectWithUploadedSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadWithUploadedVersionsSuccess>(fromActions.ProjectsActionType.LoadWithUploadedVersionsSuccess),
        switchMap((action) => {
            let events = [];
            // here we only need uploaded versions 
            events.push(new fromActions.LoadEditorAccess());
            events.push(new fromActions.LoadProjectUploadedVersions());
            return events;
        })
    ));

    public loadProjectSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadProjectSuccess>(fromActions.ProjectsActionType.LoadProjectSuccess),
        withLatestFrom(this.store$.select(fromStore.editorWorkflowTransitionsSelector), this.store$.select(fromStore.projectSelector)),
        switchMap(([action, transitions, project]) => {
            let events = [];


            events.push(new fromActions.LoadEditorWorkflowTransitions());
            events.push(new fromActions.LoadProjectFiles());
            // new fromActions.LoadProjectFilesNotes(),
            events.push(new fromActions.LoadProjectOriginVersion());
            events.push(new fromActions.LoadEditorAssigners());
            events.push(new fromActions.LoadEditorAccess());
            events.push(new fromActions.LoadProjectUploadedVersions());
            // only after transitios were loaded once
            if (transitions != null && transitions.length > 0) {
                events.push(new fromActions.SetEditorCurrentWorkflowTransitions(project.status.id));
            }

            return events;
        })
    ));


    public loadPartialProjectSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadPartialProjectSuccess>(fromActions.ProjectsActionType.LoadPartialProjectSuccess),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            let events = [];
            events.push(new fromActions.LoadEditorAccess());
            return events;
        })
    ));


    public loadProjectOriginVersion$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadProjectOriginVersion>(fromActions.ProjectsActionType.LoadProjectOriginVersion),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            return this.requests.loadOrigin$(project.id).pipe(
                map((versions: Array<Objects.Version>) => {
                    return new fromActions.LoadProjectOriginVersionSuccess(versions);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadProjectOriginVersionError(err));
                })
            );

        })
    ));


    public loadProjectFiles$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadProjectFiles>(fromActions.ProjectsActionType.LoadProjectFiles),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            return this.requests.loadFiles$(project.id).pipe(
                map((files: Array<Objects.AmsFile>) => {
                    return new fromActions.LoadProjectFilesSuccess(files);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadProjectFilesError(err));
                })
            );

        })
    ));


    public loadProjectFilesSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadProjectFilesSuccess>(fromActions.ProjectsActionType.LoadProjectFilesSuccess),
        //withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap((action) => [
            new fromActions.LoadProjectFilesNotesCounter(),
        ])
    ));


    public loadProjectMessages$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadProjectMessages>(fromActions.ProjectsActionType.LoadProjectMessages),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            return this.requests.loadMessages$(project.id).pipe(
                map((messages: Array<Objects.Message>) => {
                    return new fromActions.LoadProjectMessagesSuccess(messages);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadProjectMessagesError(err));
                })
            );

        })
    ));

    // Load a single event acording an object and an id. 

    public loadEvents$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadEvents>(fromActions.ProjectsActionType.LoadEvents),
        // withLatestFrom(this.store$.select(fromStore.projectSelector)),
        mergeMap((action) => {
            return this.requests.loadPagedEvents$(action.object, action.id).pipe(
                mergeMap((events: Array<Objects.Event>) => {
                    const actions: Array<Action> = [new fromActions.LoadEventsSuccess(events)];
                    
                    // If response has exactly 100 events, load next page
                    if (events.length === 100) {
                        actions.push(new fromActions.LoadPagedEvents(action.object, action.id, 2));
                    }
                    
                    return of(...actions);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadEventsError(err));
                })
            );

        })
    ));

    /**
     * After loading the first events, if there are more than 100 events, this effect will load the next pages starting from 2.
     * 
     * First page does not need parameter, but next pages will need the page number to load.
     */
    public loadPagedEvents$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadPagedEvents>(fromActions.ProjectsActionType.LoadPagedEvents),
        // withLatestFrom(this.store$.select(fromStore.projectSelector)),
        mergeMap((action) => {
            return this.requests.loadPagedEvents$(action.object, action.id, action.page).pipe(
                mergeMap((events: Array<Objects.Event>) => {
                    const actions: Array<Action> = [new fromActions.LoadEventsSuccess(events)];
                    
                    // If response has exactly 100 events, load next page
                    if (events.length === 100) {
                        actions.push(new fromActions.LoadPagedEvents(action.object, action.id, action.page + 1));
                    }
                    
                    return of(...actions);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadEventsError(err));
                })
            );

        })
    ));



    public loadProjectEvents$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadProjectEvents>(fromActions.ProjectsActionType.LoadProjectEvents),
        withLatestFrom(this.store$.select(fromStore.viewSelector)),
        mergeMap(([action, view]) => {
            let effectEvents = [];
            // add effect to events for the project
            effectEvents.push(new fromActions.LoadEvents('request', view.project.id));
            // console.log(" Those are the versions in project store ", view.uploaded_versions);
            if (view.uploaded_versions != null) {
                let uploadedIds = view.uploaded_versions.map((v: Objects.Version) => v.id);
                uploadedIds.forEach((vid: number) => {
                    // add effect to events for a single version
                    effectEvents.push(new fromActions.LoadEvents('version', vid));
                });
            }
            return effectEvents;

        })
    ));


    public loadProjectRelatedVersions$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadProjectRelatedVersions>(fromActions.ProjectsActionType.LoadProjectRelatedVersions),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        mergeMap(([action, project]) => { // concat force to make the requests by order to load 
            return this.requests.loadRelated$(project.id).pipe(
                map((related: Array<Objects.Version>) => {
                    return new fromActions.LoadProjectRelatedVersionsSuccess(related);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadProjectRelatedVersionsError(err));
                })
            );
        })
    ));

    public loadProjectRelatedVersionsSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadProjectRelatedVersionsSuccess>(fromActions.ProjectsActionType.LoadProjectRelatedVersionsSuccess),
        withLatestFrom(this.appStore$.pipe(select(fromAppStore.appPluginsAccessSelector(this.PLUGIN_REQUEST_FEEDBACK)))),
        switchMap(([action, pluginAccess]) => {
            let effectEvents = [];
            // add effect to load feedbacks for all related loaded
            if (pluginAccess && action.related != null && action.related.length > 0) {
                effectEvents.push(new fromActions.LoadVersionsFeedbacks(action.related));
            }
            return effectEvents;
        })
    ));

    public loadVersionsFeedbacks$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadVersionsFeedbacks>(fromActions.ProjectsActionType.LoadVersionsFeedbacks),
        withLatestFrom(this.appStore$.pipe(select(fromAppStore.appPluginsAccessSelector(this.PLUGIN_REQUEST_FEEDBACK)))),
        switchMap(([action, pluginAccess]) => {
            // check access and related to prevent wrong call.
            if (pluginAccess && action.versions != null && action.versions.length == 0) {
                return [];
            }

            return this.requests.getRelatedShareItems$(action.versions).pipe(
                map((feedbacks: VersionShareItem[]) => {
                    return new fromActions.LoadVersionsFeedbacksSuccess(feedbacks);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadVersionsFeedbacksError(err));
                })
            );
        }
        )
    ));


    public loadProjectUploadedVersions$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadProjectUploadedVersions>(fromActions.ProjectsActionType.LoadProjectUploadedVersions),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            return this.requests.loadUploadedVersions$(project.id).pipe(
                // abort this project's request as soon as we leave it: switchMap only cancels on the next
                // LoadProjectUploadedVersions, which comes after the next project has loaded.
                takeUntil(this.actions$.pipe(
                    ofType(
                        fromActions.ProjectsActionType.Clear,
                        fromActions.ProjectsActionType.LoadWithUploadedVersions,
                        fromActions.ProjectsActionType.LoadProject
                    )
                )),
                map((versions: Array<Objects.Version>) => {
                    return new fromActions.LoadProjectUploadedVersionsSuccess(versions, project.id);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadProjectUploadedVersionsError(err));
                })
            );

        })
    ));

    // Dashboard updating badge: marked when the current user launches an update, the 'request.updated' event unmarks it.
    // Only the user who launches it gets the badge, the rest of the organization just receives 'request.updated'.
    public setDashboardUpdating$ = createEffect(() => this.actions$.pipe(
        ofType(
            fromActions.ProjectsActionType.SetProjectAssigned,
            fromActions.ProjectsActionType.SetProjectDue,
            fromActions.ProjectsActionType.SetProjectStatus,
            fromActions.ProjectsActionType.SetProjectLabels,
            fromActions.ProjectsActionType.SetProjectInfo,
            fromActions.ProjectsActionType.UploadProjectFiles
        ),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => project == null ? [] : [new fromDashboardActions.SetRequestUpdating(project.id)])
    ));

    // a failed update never gets its 'request.updated' event, so the badge would keep spinning
    public clearDashboardUpdating$ = createEffect(() => this.actions$.pipe(
        ofType(
            fromActions.ProjectsActionType.SetProjectAssignedError,
            fromActions.ProjectsActionType.SetProjectDueError,
            fromActions.ProjectsActionType.SetProjectStatusError,
            fromActions.ProjectsActionType.SetProjectLabelsError,
            fromActions.ProjectsActionType.SetProjectInfoError,
            fromActions.ProjectsActionType.UploadProjectFilesError
        ),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => project == null ? [] : [new fromDashboardActions.ClearRequestUpdating(project.id)])
    ));

    // Edit
    // SetProjectInfo

    public setProjectInfo$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetProjectInfo>(fromActions.ProjectsActionType.SetProjectInfo),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            return this.requests.setInfo$(project.id, action.info).pipe(
                map(() => {
                    return new fromActions.SetProjectInfoSuccess(project.id);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SetProjectInfoError(err, project.id));
                })
            );

        })
    ));


    public setProjectInfoSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetProjectInfoSuccess>(fromActions.ProjectsActionType.SetProjectInfoSuccess),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => [
            new fromActions.LoadProject(project.id)
        ])
    ));


    public DeleteOneProjectFile$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.DeleteOneProjectFile>(fromActions.ProjectsActionType.DeleteOneProjectFile),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            return this.requests.deleteFile$(project.id, action.file).pipe(
                map((response) => {
                    console.log(" DEACTIVATE ====> ", response)
                    return new fromActions.DeleteOneProjectFileSuccess();
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.DeleteOneProjectFileError(err));
                })
            );

        })
    ));


    public DeleteOneProjectFileError$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.DeleteOneProjectFileError>(fromActions.ProjectsActionType.DeleteOneProjectFileError),
        switchMap((action) => [
            new fromActions.LoadProjectFiles()
        ])
    ));


    public DeleteOneProjectFileSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.DeleteOneProjectFileSuccess>(fromActions.ProjectsActionType.DeleteOneProjectFileSuccess),
        switchMap((action) => [
            new fromActions.LoadProjectFiles()
        ])
    ));


    public uploadProjectFile$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UploadProjectFiles>(fromActions.ProjectsActionType.UploadProjectFiles),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            return this.requests.addFiles$(project.id, action.files).pipe(
                map((files: Objects.AmsFile[]) => {
                    return new fromActions.UploadProjectFilesSuccess(files);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.UploadProjectFilesError(err));
                })
            );

        })
    ));

    
    public UploadProjectFilesSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UploadProjectFilesSuccess>(fromActions.ProjectsActionType.UploadProjectFilesSuccess),
        switchMap((action) => [
            new fromActions.LoadProjectFiles()
        ])
    ));

    
    
    public loadEditorAssigners$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadEditorAssigners>(fromActions.ProjectsActionType.LoadEditorAssigners),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            return this.requests.loadAssigners$(project.id).pipe(
                map((users: Array<Objects.User>) => {
                    return new fromActions.LoadEditorAssignersSuccess(users);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadEditorAssignersError(err));
                })
            );

        })
    ));


    public loadWorkflowTransitions$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadEditorWorkflowTransitions>(fromActions.ProjectsActionType.LoadEditorWorkflowTransitions),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            return this.requests.loadWorkflowTransitions$().pipe(
                map((transitions: Array<Objects.WorkflowTransition>) => {
                    return new fromActions.LoadEditorWorkflowTransitionsSuccess(transitions);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadEditorWorkflowTransitionsError(err));
                })
            );

        })
    ));


    public loadWorkflowTransitionsSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadEditorWorkflowTransitionsSuccess>(fromActions.ProjectsActionType.LoadEditorWorkflowTransitionsSuccess),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            if (project == null) {
                return [];
            }
            return [new fromActions.SetEditorCurrentWorkflowTransitions(project.status.id)] // filter project
        })
    ));


    public loadEditorAccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadEditorAccess>(fromActions.ProjectsActionType.LoadEditorAccess),
        withLatestFrom(this.store$.select(fromStore.projectSelector), this.store$.select(fromStore.projectHeaderSelector)),
        switchMap(([action, project, projectHeader]) => {
            const currentProject = project ?? projectHeader;
            return this.requests.loadAccess$(currentProject.id).pipe(
                map((access: Access.AccessProject) => {
                    return new fromActions.LoadEditorAccessSuccess(access);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadEditorAccessError(err));
                })
            );

        })
    ));

    // This effect loads only the access in the editor for each request.
    // used only in dashboard menu to check access to update stage
    public loadSingleAccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadEditorSingleAccess>(fromActions.ProjectsActionType.LoadEditorSingleAccess),
        // withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap((action) => {
            return this.requests.loadAccess$(action.projectId).pipe(
                map((access: Access.AccessProject) => {
                    return new fromActions.LoadEditorAccessSuccess(access);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadEditorAccessError(err));
                })
            );

        })
    ));


    public setProjectAssigned$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetProjectAssigned>(fromActions.ProjectsActionType.SetProjectAssigned),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            return this.requests.setAssign$(project.id, action.user).pipe(
                map((project: Objects.Request) => {
                    return new fromActions.SetProjectAssignedSuccess(project);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SetProjectAssignedError(err));
                })
            );

        })
    ));


    public setProjectAssignedError$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetProjectAssignedError>(fromActions.ProjectsActionType.SetProjectAssignedError),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => [
            new fromActions.LoadProject(project.id)
        ])
    ));


    public setProjectDue$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetProjectDue>(fromActions.ProjectsActionType.SetProjectDue),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            return this.requests.setDue$(project.id, action.date).pipe(
                map((project: Objects.Request) => {
                    return new fromActions.SetProjectDueSuccess(project);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SetProjectDueError(err));
                })
            );

        })
    ));


    public setProjectDueError$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetProjectDueError>(fromActions.ProjectsActionType.SetProjectDueError),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => [
            new fromActions.LoadProject(project.id)
        ])
    ));


    public setProjectStatus$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetProjectStatus>(fromActions.ProjectsActionType.SetProjectStatus),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            return this.requests.setStatus$(project.id, action.status).pipe(
                map((project: Objects.Request) => {
                    return new fromActions.SetProjectStatusSuccess(project);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SetProjectStatusError(err));
                })
            );

        })
    ));


    public setProjectStatusSuccess$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetProjectStatusSuccess>(fromActions.ProjectsActionType.SetProjectStatusSuccess),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            if (project == null) {
                return [];
            }
            return [
                // project already has new status id here
                new fromActions.SetEditorCurrentWorkflowTransitions(project.status.id),
                new fromActions.LoadProject(project.id)
            ] // reload project

            // new fromActions.LoadEditorAssigners(),
            // new fromActions.LoadEditorAccess(),
            // new fromActions.LoadEditorWorkflowTransitions(),
        })
    ));


    public setProjectStatusError$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetProjectStatusError>(fromActions.ProjectsActionType.SetProjectStatusError),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => [
            new fromActions.LoadProject(project.id)
        ])
    ));



    public setProjectLabels$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetProjectLabels>(fromActions.ProjectsActionType.SetProjectLabels),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        concatMap(([action, project]) => {
            return this.requests.setLabels$(project.id, action.labels).pipe(
                map((project: Objects.Request) => {
                    return new fromActions.SetProjectLabelsSuccess(project);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SetProjectLabelsError(err));
                })
            );

        })
    ));

    public setProjectLabelsError$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetProjectLabelsError>(fromActions.ProjectsActionType.SetProjectLabelsError),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => [
            new fromActions.LoadProject(project.id)
        ])
    ));


    public loadProjectFilesNotesCounter$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadProjectFilesNotesCounter>(fromActions.ProjectsActionType.LoadProjectFilesNotesCounter),
        withLatestFrom(this.store$.select(fromStore.projectSelector)),
        switchMap(([action, project]) => {
            if (project == null) {
                return [];
            }
            return this.requests.loadFilesNotesCounter$(project.id).pipe(
                map((notes: Array<Objects.AnnotationsCounter>) => {
                    return new fromActions.LoadProjectFilesNotesCounterSuccess(notes);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.LoadProjectFilesNotesCounterError(err));
                })
            );

        })
    ));


    public setRelatedVersionStatus$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetRelatedVersionStatus>(fromActions.ProjectsActionType.SetRelatedVersionStatus),
        // withLatestFrom(this.store$.select(fromStore.viewSelector)),
        switchMap((action) => {
            return this.requests.setVersionStatus$(action.version.id, action.status).pipe(
                map((version: Objects.Version) => {
                    return new fromActions.SetRelatedVersionStatusSuccess(version);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SetRelatedVersionStatusError(err, action.groupId, action.version));
                })
            );

        })
    ));

    public setUploadedVersionStatus$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetUploadedVersionStatus>(fromActions.ProjectsActionType.SetUploadedVersionStatus),
        // withLatestFrom(this.store$.select(fromStore.viewSelector)),
        switchMap((action) => {
            return this.requests.setVersionStatus$(action.version.id, action.status).pipe(
                map((version: Objects.Version) => {
                    return new fromActions.SetUploadedVersionStatusSuccess(version);
                }),
                catchError((err: any) => {
                    console.log(err);
                    throwError(() => err);
                    return of(new fromActions.SetUploadedVersionStatusError(err, action.version));
                })
            );

        })
    ));

    // Effects dispatched by WS
    public updateProject$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateProject>(fromActions.ProjectsActionType.UpdateProject),
        withLatestFrom(this.store$.select(fromStore.viewSelector)),
        mergeMap(([action, view]) => { // merge force to do all
            let events = [];

            if ((view.project != null && view.project.id == action.projectId) ||
                (view.header != null && view.header.id == action.projectId)) {
                // console.log("update request", action);
                events.push(new fromActions.LoadProject(action.projectId));
            }

            return events;
        })
    ));

    //  update project messages if event is for my project id
    public updateProjectMessages$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateProjectMessages>(fromActions.ProjectsActionType.UpdateProjectMessages),
        withLatestFrom(this.store$.select(fromStore.viewSelector)),
        mergeMap(([action, view]) => { // merge force to do all
            let events = [];
            // not for header when no access to the project
            if ((view.project != null && view.project.id == action.projectId)) {
                // console.debug("updating request messages", action);
                events.push(new fromActions.LoadProjectMessages());
            }
            return events;
        })
    ));

    //  update project related version feedbacks if event is for any related version id
    public updateVersionFeedbacks$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateVersionsFeedbacks>(fromActions.ProjectsActionType.UpdateVersionsFeedbacks),
        withLatestFrom(this.store$.select(fromStore.projectRelatedVersionsSelector)),
        mergeMap(([action, related]) => { // merge force to do all
            let events = [];
            if (related != null) {
                // Find the Version to update in related based on versionId
                const foundVersion = related.map((rv: Objects.RelatedVersions) => rv.versions.find((vr: Objects.Version) => vr.id === action.versionId))
                    .find((version: Objects.Version) => version !== undefined);
                if (foundVersion)
                    events.push(new fromActions.LoadVersionsFeedbacks([foundVersion]));
            }
            return events;
        })
    ));


    //  update project related versions if event is for any related version id
    // careful: it only works in negative, 
    // if a version update status and you have no acces to it and you have the version on the lsit it'll update, 
    // but if you dont have it loaded and changes to a status you have acces, this will do do nothing, will not reload related
    public updateRelatedVersion$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.UpdateRelatedVersion>(fromActions.ProjectsActionType.UpdateRelatedVersion),
        withLatestFrom(this.store$.select(fromStore.projectRelatedVersionsSelector)),
        mergeMap(([action, related]) => { // merge force to do all
            let events = [];
            if (related != null) {
                // Find the updated Version to update related based on versionId on the event
                const foundVersion = related.map((rv: Objects.RelatedVersions) => rv.versions.find((vr: Objects.Version) => vr.id === action.versionId))
                    .find((version: Objects.Version) => version !== undefined);
                if ((foundVersion)) {
                    events.push(new fromActions.LoadProjectRelatedVersions());
                }
            }
            return events;
        })
    ));

    public updateNewRelatedVersion$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadProjectNewRelatedVersions>(fromActions.ProjectsActionType.LoadProjectNewRelatedVersions),
        withLatestFrom(this.store$.select(fromStore.viewSelector)),
        mergeMap(([action, view]) => { // merge force to do all
            let events = [];
            if (action.project != null && action.version != null && view.project != null && view.project.id == action.project) {
                events.push(new fromActions.LoadProjectUploadedVersions());
                events.push(new fromActions.LoadProjectRelatedVersions());
            }
            //  reload related versions if event is from this project to capture new version added
            return events;
        })
    ))

    // Logbook
    public applySearchFilter$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.ApplyLogbookSearchFilter>(fromActions.ProjectsActionType.ApplyLogbookSearchFilter),
        switchMap((action) => [
            new fromActions.UpdateLogbookResults()
        ])
    ));


    public removeSearchFilter$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.RemoveLogbookSearchFilter>(fromActions.ProjectsActionType.RemoveLogbookSearchFilter),
        switchMap((action) => [
            new fromActions.UpdateLogbookResults()
        ])
    ));

    public setLogbookSearchTerm$ = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SetLogbookSearchTerm>(fromActions.ProjectsActionType.SetLogbookSearchTerm),
        switchMap((action) => [
            new fromActions.UpdateLogbookResults()
        ])
    ));

}